import { useEffect, useState } from "react";
import { IconMessageCircle, IconSend } from "@tabler/icons-react";
import { Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Button, Input, Text } from "@fragments";
import { UserAvatar } from "@components/Community";
import { COMMENT_MAX_LENGTH } from "@helpers/posts";

// The counter only shows once the text is long enough to matter.
const COUNTER_FROM = COMMENT_MAX_LENGTH - 200;

/**
 * 02.B — the thread (scrolls) over the composer (fixed at the bottom, parted
 * by a side-to-side hairline). Newest comments come first, like the API.
 */
export default function Comments({
    comments,
    me,
    ready,
    sending,
    composerRef,
    onSend,
    onLoadMore,
}) {
    const { t } = useTranslation("groupModals");
    const [draft, setDraft] = useState("");
    // `Input.Text` seeds its own state from `value` once, on mount, so the
    // field is remounted (new key) to clear it after a comment goes out.
    const [fieldKey, setFieldKey] = useState(0);

    useEffect(() => {
        if (fieldKey === 0) return;
        composerRef?.current?.querySelector("input, textarea")?.focus();
    }, [fieldKey, composerRef]);

    const { items, total, page, lastPage, loading, error } = comments;
    const trimmed = draft.trim();
    const oversized = trimmed.length > COMMENT_MAX_LENGTH;
    const canSend = ready && !sending && trimmed.length > 0 && !oversized;
    const hasMore = page < lastPage;
    const initialLoading = loading && items.length === 0;

    async function submit() {
        if (!canSend) return;
        const sent = await onSend(trimmed);
        if (sent) {
            setDraft("");
            setFieldKey((k) => k + 1);
        }
    }

    function onKeyDown(e) {
        if (e.key === "Enter") {
            e.preventDefault();
            submit();
        }
    }

    return (
        <section className="post-modal__panel post-comments">
            <header className="post-comments__header">
                <Text.h5 weight={500} contrast={9} left={<IconMessageCircle />}>
                    {t("postModal.comments.title", { count: total })}
                </Text.h5>
            </header>

            <div className="post-comments__scroll">

                {initialLoading && (
                    <ul className="post-comments__list" aria-hidden="true">
                        {[0, 1, 2].map((i) => (
                            <li key={i} className="post-comment" style={{ "--i": i }}>
                                <Skeleton variant="circular" width={32} height={32} className="pm-skeleton" />
                                <div className="post-comment__body">
                                    <Skeleton variant="text" width={120} height={14} className="pm-skeleton" />
                                    <Skeleton variant="text" width="90%" height={14} className="pm-skeleton" />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {!initialLoading && !error && items.length === 0 && (
                    <div className="post-comments__empty">
                        <Text.span contrast={6}>{t("postModal.comments.empty")}</Text.span>
                    </div>
                )}

                {error && items.length === 0 && (
                    <div className="post-comments__empty">
                        <Text.span contrast={6}>{t("postModal.comments.loadError")}</Text.span>
                    </div>
                )}

                {items.length > 0 && (
                    <ul className="post-comments__list">
                        {items.map((comment, i) => (
                            <li key={comment.id || i} className="post-comment" style={{ "--i": i }}>
                                <UserAvatar user={comment.publisher} size={32} />
                                <div className="post-comment__body">
                                    <div className="post-comment__head">
                                        <Text.label contrast={9} className="post-comment__name">
                                            {comment.publisher?.username}
                                        </Text.label>
                                        <Text.caption contrast={5}>
                                            {window.helpers.pretty.date(comment.timestamps?.created)}
                                        </Text.caption>
                                    </div>
                                    <Text.span contrast={8} className="post-comment__text">
                                        {comment.payload?.text}
                                    </Text.span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {hasMore && (
                    <Button
                        variant="text"
                        color="inverse"
                        size="small"
                        fullWidth
                        loading={loading && items.length > 0}
                        onClick={onLoadMore}
                        className="post-comments__more"
                    >
                        {t("postModal.comments.loadMore")}
                    </Button>
                )}
            </div>

            <div className="post-comments__composer" ref={composerRef}>
                <UserAvatar user={me} size={32} link={false} />
                <div className="post-comments__field">
                    <Input.Text
                        key={fieldKey}
                        className="post-comments__input"
                        value={draft}
                        onInput={setDraft}
                        onKeyDown={onKeyDown}
                        placeholder={t("postModal.comments.placeholder")}
                        disabled={!ready || sending}
                        error={oversized}
                        emoji
                    />
                    {trimmed.length >= COUNTER_FROM && (
                        <Text.caption contrast={oversized ? 10 : 5} className={`post-comments__counter${oversized ? " post-comments__counter--over" : ""}`}>
                            {trimmed.length}/{COMMENT_MAX_LENGTH}
                        </Text.caption>
                    )}
                </div>
                <Button
                    variant="rounded"
                    color="inverse"
                    size="small"
                    icon={<IconSend />}
                    onClick={submit}
                    disabled={!canSend}
                    loading={sending}
                    tooltip={t("postModal.comments.send")}
                    aria-label={t("postModal.comments.send")}
                    className="post-comments__send"
                />
            </div>
        </section>
    );
}
